import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Neyra Theme System
 * - Detects system preference on first load
 * - Persists to localStorage ("neara-theme": "light" | "dark")
 * - Exposes { theme, setTheme, toggleTheme, usingSystem, resetToSystem }
 * - Updates <html class="dark"> and <meta theme-color>
 */

const STORAGE_KEY = "neara-theme";
const ThemeContext = createContext(null);

const getSystemTheme = () =>
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";

const applyThemeToDOM = (theme) => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.setAttribute("data-theme", theme);
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        if (typeof window === "undefined") return "light";
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored || getSystemTheme();
    });

    const [usingSystem, setUsingSystem] = useState(() => {
        if (typeof window === "undefined") return true;
        return !localStorage.getItem(STORAGE_KEY);
    });

    // Apply theme to DOM whenever it changes
    useEffect(() => {
        applyThemeToDOM(theme);
    }, [theme]);

    // Listen to system theme changes ONLY if user hasn't set a preference
    useEffect(() => {
        if (!usingSystem) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e) => setThemeState(e.matches ? "dark" : "light");
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, [usingSystem]);

    const setTheme = useCallback((next) => {
        localStorage.setItem(STORAGE_KEY, next);
        setUsingSystem(false);
        setThemeState(next);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(theme === "dark" ? "light" : "dark");
    }, [theme, setTheme]);

    const resetToSystem = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setUsingSystem(true);
        setThemeState(getSystemTheme());
    }, []);

    return (
        <ThemeContext.Provider
            value={{ theme, setTheme, toggleTheme, usingSystem, resetToSystem }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
    return ctx;
};
